import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const Providers = {
  list: () => api.get("/providers").then((r) => r.data),
  create: (payload) => api.post("/providers", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/providers/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/providers/${id}`).then((r) => r.data),
};

export const Days = {
  list: () => api.get("/days").then((r) => r.data),
  get: (date) => api.get(`/days/${date}`).then((r) => r.data),
  diff: (date, vs) =>
    api
      .get(`/days/${date}/diff`, { params: vs ? { vs } : {} })
      .then((r) => r.data),
  generate: (payload) => api.post("/days/generate", payload).then((r) => r.data),
  updatePatient: (date, patientId, payload) =>
    api.put(`/days/${date}/patients/${patientId}`, payload).then((r) => r.data),
  addPatient: (date, patient) =>
    api.post(`/days/${date}/patients`, patient).then((r) => r.data),
  removePatient: (date, patientId) =>
    api.delete(`/days/${date}/patients/${patientId}`).then((r) => r.data),
  setPhp: (date, provider_id) =>
    api.post(`/days/${date}/php`, { provider_id }).then((r) => r.data),
  setRoles: (date, payload) =>
    api.put(`/days/${date}/roles`, payload).then((r) => r.data),
  updateMedicalTasks: (date, payload) =>
    api.put(`/days/${date}/medical-tasks`, payload).then((r) => r.data),
  setRoundingTime: (date, provider_id, time) =>
    api.put(`/days/${date}/rounding-time`, { provider_id, time }).then((r) => r.data),
  setShuffle: (date, payload) =>
    // { quote_offset?, tip_offset? } — persists the per-day shuffle so
    // exports pick up the same quote/tip the user shuffled to on-screen.
    api.put(`/days/${date}/shuffle`, payload).then((r) => r.data),
  reassignAll: (date, from_provider_id, to_provider_id) =>
    api.post(`/days/${date}/reassign`, { from_provider_id, to_provider_id }).then((r) => r.data),
  remove: (date) => api.delete(`/days/${date}`).then((r) => r.data),
  reset: (scope = "all") => api.post("/reset", { scope }).then((r) => r.data),
  exportXlsx: (date) => `${API_BASE}/days/${date}/export/xlsx`,
  exportDocx: (date) => `${API_BASE}/days/${date}/export/docx`,
  exportProviderXlsx: (date, providerId) =>
    `${API_BASE}/days/${date}/providers/${providerId}/export/xlsx`,
  exportProviderDocx: (date, providerId) =>
    `${API_BASE}/days/${date}/providers/${providerId}/export/docx`,
};

/**
 * Trigger a browser download of an authenticated API URL.
 * Fetches with credentials so the session cookie is sent, then
 * saves the resulting blob using a temporary <a download>.
 * More reliable than `window.open` (which some browsers open then
 * strand as a blank tab when the response is a file attachment).
 */
export async function downloadAuthed(url, filename) {
  const res = await api.get(url.replace(API_BASE, ""), { responseType: "blob" });
  const blob = res.data;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on next tick so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export const Census = {
  parse: (text) => api.post("/parse-census", { text }).then((r) => r.data),
};

export const Documentation = {
  compliance: (date) =>
    api.get("/quality/documentation-compliance", { params: date ? { date } : {} }).then((r) => r.data),
  signoff: (date, patientId, event, initials, clear = false) =>
    api.post(`/days/${date}/patients/${patientId}/documentation`, {
      event,
      initials,
      clear,
    }).then((r) => r.data),
};

export const Committees = {
  getFacility: () => api.get("/committees/facility").then((r) => r.data),
  updateFacility: (payload) => api.put("/committees/facility", payload).then((r) => r.data),
  list: () => api.get("/committees").then((r) => r.data),
  get: (id) => api.get(`/committees/${id}`).then((r) => r.data),
  update: (id, payload) => api.put(`/committees/${id}`, payload).then((r) => r.data),
  listMembers: (id) => api.get(`/committees/${id}/members`).then((r) => r.data),
  createMember: (id, payload) => api.post(`/committees/${id}/members`, payload).then((r) => r.data),
  updateMember: (id, memberId, payload) =>
    api.put(`/committees/${id}/members/${memberId}`, payload).then((r) => r.data),
  deleteMember: (id, memberId) => api.delete(`/committees/${id}/members/${memberId}`).then((r) => r.data),
  listMeetings: (id) => api.get(`/committees/${id}/meetings`).then((r) => r.data),
  createMeeting: (id, payload) => api.post(`/committees/${id}/meetings`, payload).then((r) => r.data),
  getMeeting: (id, meetingId) =>
    api.get(`/committees/${id}/meetings/${meetingId}`).then((r) => r.data),
  updateMeeting: (id, meetingId, payload) =>
    api.put(`/committees/${id}/meetings/${meetingId}`, payload).then((r) => r.data),
  updateSection: (id, meetingId, payload) =>
    api.put(`/committees/${id}/meetings/${meetingId}/section`, payload).then((r) => r.data),
  updateAttendance: (id, meetingId, marks) =>
    api.put(`/committees/${id}/meetings/${meetingId}/attendance`, marks).then((r) => r.data),
  addSection: (id, meetingId, payload) =>
    api.post(`/committees/${id}/meetings/${meetingId}/sections`, payload).then((r) => r.data),
  renameSection: (id, meetingId, sectionId, title) =>
    api.put(`/committees/${id}/meetings/${meetingId}/sections/${sectionId}/rename`, { title }).then((r) => r.data),
  deleteSection: (id, meetingId, sectionId) =>
    api.delete(`/committees/${id}/meetings/${meetingId}/sections/${sectionId}`).then((r) => r.data),
  addItem: (id, meetingId, sectionId, title) =>
    api.post(`/committees/${id}/meetings/${meetingId}/sections/${sectionId}/items`, { title }).then((r) => r.data),
  renameItem: (id, meetingId, sectionId, itemId, title) =>
    api.put(`/committees/${id}/meetings/${meetingId}/sections/${sectionId}/items/${itemId}/rename`, { title }).then((r) => r.data),
  deleteItem: (id, meetingId, sectionId, itemId) =>
    api.delete(`/committees/${id}/meetings/${meetingId}/sections/${sectionId}/items/${itemId}`).then((r) => r.data),
  createSectionShare: (id, meetingId, sectionId, label = "", expires_at = "") =>
    api.post(`/committees/${id}/meetings/${meetingId}/section-shares`, {
      section_id: sectionId, label, expires_at,
    }).then((r) => r.data),
  listSectionShares: (id, meetingId, section_id) =>
    api.get(`/committees/${id}/meetings/${meetingId}/section-shares`, { params: section_id ? { section_id } : {} }).then((r) => r.data),
  revokeSectionShare: (linkId) =>
    api.delete(`/committees/section-shares/${linkId}`).then((r) => r.data),
  sharedCommitteeUrl: (token) => `${window.location.origin}/shared/committee/${token}`,
  listActionItems: (id) => api.get(`/committees/${id}/action-items`).then((r) => r.data),
  createActionItem: (id, meetingId, payload) =>
    api.post(`/committees/${id}/action-items?meeting_id=${meetingId}`, payload).then((r) => r.data),
  updateActionItem: (id, itemId, payload) =>
    api.put(`/committees/${id}/action-items/${itemId}`, payload).then((r) => r.data),
  deleteActionItem: (id, itemId) => api.delete(`/committees/${id}/action-items/${itemId}`).then((r) => r.data),
  meetingDocxUrl: (id, meetingId) =>
    `${API_BASE}/committees/${id}/meetings/${meetingId}/export/docx`,
  meetingPdfUrl: (id, meetingId) =>
    `${API_BASE}/committees/${id}/meetings/${meetingId}/export/pdf`,
  generateAiMinutes: (id, meetingId, refresh = false) =>
    api.post(`/committees/${id}/meetings/${meetingId}/ai-minutes`, null, {
      params: refresh ? { refresh: true } : {},
    }).then((r) => r.data),
};

export const UNITS = ["200", "300", "400", "500", "600"];

// ---------------------------------------------------------------------------
// Quality Command — weekly quality-indicator workspace
// ---------------------------------------------------------------------------
export const Quality = {
  listDirectors: () => api.get("/quality/directors").then((r) => r.data),
  createDirector: (payload) => api.post("/quality/directors", payload).then((r) => r.data),
  updateDirector: (id, payload) => api.put(`/quality/directors/${id}`, payload).then((r) => r.data),
  deleteDirector: (id) => api.delete(`/quality/directors/${id}`).then((r) => r.data),

  listIndicators: (director_id) =>
    api.get("/quality/indicators", { params: director_id ? { director_id } : {} }).then((r) => r.data),
  createIndicator: (payload) => api.post("/quality/indicators", payload).then((r) => r.data),
  updateIndicator: (id, payload) => api.put(`/quality/indicators/${id}`, payload).then((r) => r.data),
  deleteIndicator: (id) => api.delete(`/quality/indicators/${id}`).then((r) => r.data),

  report: (params) => api.get("/quality/report", { params }).then((r) => r.data),
  upsertChecks: (items) => api.put("/quality/checks", { items }).then((r) => r.data),
  monthly: (month) => api.get("/quality/monthly", { params: { month } }).then((r) => r.data),
  monthlyXlsxUrl: (month) => `${API_BASE}/quality/monthly/export/xlsx?month=${encodeURIComponent(month)}`,
  monthlyPdfUrl: (month) => `${API_BASE}/quality/monthly/export/pdf?month=${encodeURIComponent(month)}`,

  listSurveyPrep: () => api.get("/quality/survey-prep").then((r) => r.data),
  createSurveyItem: (payload) => api.post("/quality/survey-prep", payload).then((r) => r.data),
  updateSurveyItem: (id, payload) => api.put(`/quality/survey-prep/${id}`, payload).then((r) => r.data),
  deleteSurveyItem: (id) => api.delete(`/quality/survey-prep/${id}`).then((r) => r.data),

  listCaps: (params) => api.get("/quality/caps", { params }).then((r) => r.data),
  saveCap: (payload) => api.post("/quality/caps", payload).then((r) => r.data),
  updateCap: (id, payload) => api.put(`/quality/caps/${id}`, payload).then((r) => r.data),
  listTasks: (params) => api.get("/quality/tasks", { params }).then((r) => r.data),
  createTask: (payload) => api.post("/quality/tasks", payload).then((r) => r.data),
  updateTask: (id, payload) => api.put(`/quality/tasks/${id}`, payload).then((r) => r.data),
  deleteTask: (id) => api.delete(`/quality/tasks/${id}`).then((r) => r.data),
  exportXlsxUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/quality/report/export/xlsx?${qs}`;
  },

  // ---- Evidence Engine ----
  createSnapshot: (payload) => api.post("/quality/snapshots", payload).then((r) => r.data),
  listSnapshots: (params) => api.get("/quality/snapshots", { params }).then((r) => r.data),
  getSnapshot: (id) => api.get(`/quality/snapshots/${id}`).then((r) => r.data),
  signSnapshot: (id, signed_by) =>
    api.post(`/quality/snapshots/${id}/sign`, { signed_by }).then((r) => r.data),
  timeline: (date) => api.get("/quality/timeline", { params: { date } }).then((r) => r.data),
  audit: (params) => api.get("/quality/audit", { params }).then((r) => r.data),

  createShareLink: (payload) => api.post("/quality/share-links", payload).then((r) => r.data),
  listShareLinks: (params) => api.get("/quality/share-links", { params }).then((r) => r.data),
  revokeShareLink: (id) => api.delete(`/quality/share-links/${id}`).then((r) => r.data),
  sharedUrl: (token) => `${window.location.origin}/shared/${token}`,

  digest: (date) => api.get("/quality/digest", { params: { date } }).then((r) => r.data),
  digestXlsxUrl: (date) => `${API_BASE}/quality/digest/export/xlsx?date=${encodeURIComponent(date)}`,
  digestPdfUrl: (date) => `${API_BASE}/quality/digest/export/pdf?date=${encodeURIComponent(date)}`,

  insights: (month, refresh = false) =>
    api.get("/quality/monthly/insights", { params: { month, refresh } }).then((r) => r.data),
  boardOnePagerUrl: (month, signer = "") =>
    `${API_BASE}/quality/monthly/board-one-pager/pdf?month=${encodeURIComponent(month)}${signer ? `&signer=${encodeURIComponent(signer)}` : ""}`,
};

export const Flash = {
  forms: () => api.get("/flash/forms").then((r) => r.data),
  createForm: (payload) => api.post("/flash/forms", payload).then((r) => r.data),
  updateForm: (id, payload) => api.put(`/flash/forms/${id}`, payload).then((r) => r.data),
  reorderForms: (ids) => api.put("/flash/order", { ids }).then((r) => r.data),
  reset: (include_notes) => api.post("/flash/reset", { include_notes }).then((r) => r.data),
  removeForm: (id) => api.delete(`/flash/forms/${id}`).then((r) => r.data),
  entries: (date) => api.get("/flash/entries", { params: { date } }).then((r) => r.data),
  saveEntries: (payload) => api.put("/flash/entries", payload).then((r) => r.data),
  dashboard: (date) => api.get("/flash/dashboard", { params: { date } }).then((r) => r.data),
  comments: (formId) => api.get(`/flash/forms/${formId}/comments`).then((r) => r.data),
  addComment: (formId, text) => api.post(`/flash/forms/${formId}/comments`, { text }).then((r) => r.data),
  removeComment: (id) => api.delete(`/flash/comments/${id}`).then((r) => r.data),
  pdf: (date) => downloadAuthed(`/flash/dashboard/pdf?date=${date}`, `daily-flash-${date}.pdf`),
  notesPdf: (month) => downloadAuthed(`/flash/notes/pdf?month=${month}`, `flash-owner-notes-${month}.pdf`),
};
